import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useDeferredValue, useEffect, useRef } from "react";
import { DashboardListing } from "../../component/DashboardListing";
import { SectionContainer } from "../../component/SectionContainer";
import { DashboardEditor } from "./component/Editor";
import { DashboardEditorContext, useCreateDashboardEditorContext } from "../../hook/context/editor";
import BlankStateV2 from "@ui/BlankStateV2";
import { Dashboard } from "@ui/Illustration/react";
import t from "../../../../../i18n";
import { DashboardReadonlyHeader } from "./component/Header/ReadonlyHeader";
import { DashboardReadWriteHeader } from "./component/Header/ReadWriteHeader";
import { useDashboardListingFolding } from "../../hook/useDashboardListingFolding";
import { printableMain, printablePage } from "../../utils/print";
import { useDashboardUnsaved } from "../../hook/useDashboardUnsaved";
import { useLockDashboardCallback, useUnlockDashboardCallback } from "../../hook/action/lock";
import { useParams } from "react-router";
import { Actions } from "./component/Header/Actions";
import { ModalDashboardTakeOver } from "../../component/TransactionalModals/ModalDashboardTakeOver";
import { useTranslations } from "../../../../../api/useTranslations";
import { useMatchPattern } from "../../../../../router/helpers/path";
import { DashboardContent } from "./component/Content";
import { useDashboard } from "../../hook/api/dashboard";
import { useDashboardEditorState } from "../../hook/store/editor";
import { DASHBOARD_LOCK_REFRESH_INTERVAL_MS } from "diagnostics-domain/dashboard/lock";
import { spacings } from "@ui/Theme";

const positionnedDashboardEditor = css`
  align-self: start;
  grid-column: 3 / span 1;
  grid-row: 1 / span 1;
`;

export const dashboardContent = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  row-gap: 16px;
  padding: 0 0 ${spacings.xxl} 0;
  margin: 0 auto;
  width: 100%;

  &:not(.lite) {
    padding-left: ${spacings.l};
    padding-right: 0;
  }

  &.editable {
    padding-left: 0;
  }

  > * {
    grid-column: 1 / span 1;
  }
`;

const sticky = css`
  align-self: start;
  position: sticky;
  top: var(--primary-header-height);
`;

/** @note export default required for React.lazy */
export default memo(() => {
  const { projectId, dashboardId } = useParams();
  if (!projectId) throw new Error("no project");
  if (!dashboardId) throw new Error("no dashboard");

  const matchDashboardEdit = useMatchPattern("/projects/:projectId/dashboards/:dashboardId/edit");
  const editable = Boolean(matchDashboardEdit);
  const deferredContentEdit = useDeferredValue(matchDashboardEdit);
  const translations = useTranslations();

  const { dashboard, blocks } = useDashboard(projectId, dashboardId);

  const editor = useCreateDashboardEditorContext({
    dashboardId,
    projectId,
  });
  const editorState = useDashboardEditorState(editor?.store);
  const [, setDashboardListingFolded] = useDashboardListingFolding({});
  const [, setUnsaved] = useDashboardUnsaved({
    scope: dashboardId,
  });
  const lockDashboard = useLockDashboardCallback(projectId);
  const unlockDashboard = useUnlockDashboardCallback(projectId);
  const scrollPosition = useRef<number>(window.scrollY);

  useEffect(() => {
    if (!editorState?.dashboard && dashboard) {
      void editor.setup({ dashboard, blocks });
    }
    // eslint-disable-next-line
  }, [editor, editorState?.dashboard, dashboard]);

  const deferEditable = useDeferredValue(editable);

  useEffect(
    () => () => void (deferEditable && unlockDashboard(dashboardId)),
    [deferEditable, dashboardId, unlockDashboard],
  );

  useEffect(() => {
    /**
     * @note con't know and trigger a request on user leaving the page
     * so we will disable prompt to prevent locked dashboard. Need to find a better solution.
     */
    if (editable) {
      void lockDashboard(dashboardId);
      const beforeUnload = () => {
        void unlockDashboard(dashboardId);
      };
      const refreshOnVisible = () => {
        if (document.visibilityState === "visible") {
          void lockDashboard(dashboardId);
        }
      };
      const refreshInterval = window.setInterval(() => {
        void lockDashboard(dashboardId);
      }, DASHBOARD_LOCK_REFRESH_INTERVAL_MS);
      window.addEventListener("beforeunload", beforeUnload);
      document.addEventListener("visibilitychange", refreshOnVisible);
      setDashboardListingFolded(s => s || true);
      scrollPosition.current = window.scrollY;
      return () => {
        window.removeEventListener("beforeunload", beforeUnload);
        document.removeEventListener("visibilitychange", refreshOnVisible);
        window.clearInterval(refreshInterval);
      };
    }
    window.scrollTo({ top: scrollPosition.current, behavior: "instant" });
    return () => {
      scrollPosition.current = window.scrollY;
    };
  }, [
    dashboardId,
    editable,
    setDashboardListingFolded,
    setUnsaved,
    unlockDashboard,
    lockDashboard,
  ]);

  return (
    <DashboardEditorContext.Provider value={editor}>
      <SectionContainer editable={editable} className={printablePage}>
        <DashboardListing projectId={projectId} dashboardId={dashboardId} className={sticky} />
        {dashboard ? (
          <main className={cx(dashboardContent, printableMain, { editable })}>
            {deferredContentEdit ? (
              <DashboardReadWriteHeader dashboardId={dashboardId} projectId={projectId} />
            ) : (
              <DashboardReadonlyHeader projectId={projectId} dashboardId={dashboardId}>
                <Actions projectId={projectId} dashboardId={dashboardId} />
              </DashboardReadonlyHeader>
            )}
            <DashboardContent editable={editable} projectId={projectId} dashboardId={dashboardId} />
          </main>
        ) : (
          <BlankStateV2
            picto={<Dashboard size="l" />}
            title={t("project.dashboard.found.error", translations)}
          />
        )}
        {editable && (
          <>
            <DashboardEditor
              projectId={projectId}
              className={cx(sticky, positionnedDashboardEditor)}
            />
            <ModalDashboardTakeOver projectId={projectId} dashboardId={dashboardId} />
          </>
        )}
      </SectionContainer>
    </DashboardEditorContext.Provider>
  );
});
